// MobileLanding.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import PitchPage from "../../pages/standalone/PitchPage";
import LPApplication from "../../pages/standalone/LPApplication";
import ResourceNavigator from "../resources/ResourceNavigator";
import MobileBuddyMessenger from "../community/MobileBuddyMessenger";
import MobileLPPortal from "../lp/MobileLPPortal";
import BootScreen from "./BootScreen";
import HitCounter from "../ui/HitCounter";
import OSIcon32 from "../icons/OSIcon32";
import ChapterDonationChooser from "../donate/ChapterDonationChooser";
import { collection, query, getDocs } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { useLandingImpactStats } from "../../helpers/impactStats";
import {
  SEO_H1,
  HOME_HERO,
  MISSION,
  IMPACT,
  HOW_IT_WORKS,
  HOW_IT_WORKS_STEPS,
  PRESS,
  PRESS_ITEMS,
  AWARDEES_MARQUEE_HEAD,
  AWARDEES_MARQUEE,
  TESTIMONIAL,
  FINAL_CTA,
  FOOTER_LINKS,
  COPYRIGHT_NOTICE,
  LEGACY_CHAPTER_CARDS,
  CHAPTERS_HERO,
  CHAPTER_CARD_CTAS,
  HOW_CHAPTERS_WORK,
  COLLECTIVE_GIVING,
  START_A_CHAPTER,
  AWARDEES_PAGE,
  DONATE_HERO,
  GIVING,
  ONE_TIME_GIVING,
  CLUB_MEMBERSHIP,
  SPONSORSHIP,
  SPONSORSHIP_TIERS,
  COFOUNDER_NOTE,
} from "../../data/landingContent";
import "../../styles/App.css";

// ---- Mobile-only abbreviated copy -------------------------------------------
// The mobile landing intentionally shortens some of the shared desktop copy.
// The canonical/full strings live in src/data/landingContent.js; only the
// abbreviated variants are kept local here, keyed against the shared entries.
const MOBILE_STAT_LABELS = {
  "Women-owned businesses": "Women-owned",
  "BIPOC-owned businesses": "BIPOC-owned",
  "In micro-grants awarded": "In micro-grants",
};

const MOBILE_STEP_COPY = {
  "02": "Each chapter's LPs review every submission at the end of each quarter.",
};
const MOBILE_HOW_IT_WORKS_STEPS = HOW_IT_WORKS_STEPS.map((s) => ({ ...s, copy: MOBILE_STEP_COPY[s.n] || s.copy }));

const MOBILE_AWARDEE_ALTS = {
  "/assets/Ernies2.webp": "Ernie's Pop Shop - Buffalo grant recipient",
  "/assets/BFR2.webp": "Buffalo Fashion Runway - grant recipient",
  "/assets/Trinas2.webp": "Trina's Speedy Cleaning - grant recipient",
  "/assets/gnf-kamil.webp": "Signing is Art - grant recipient",
  "/assets/muuvya.webp": "Muuvya - grant recipient",
};
const MOBILE_AWARDEE_MARQUEE = AWARDEES_MARQUEE.map((img) => ({ src: img.src, alt: MOBILE_AWARDEE_ALTS[img.src] }));

const AWARDEES_CACHE_KEY = "gnf:awardees:v1";

const loadCachedAwardees = () => {
  try {
    const cached = sessionStorage.getItem(AWARDEES_CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch {
    return [];
  }
};

// ---- Start menu — mirrors the desktop taskbar's launcher --------------------
// Navigator sections switch the tab inside the Navigator window; apps open
// full-screen programs or overlays. Same markup/classes as DesktopTaskbar.
const START_SECTIONS = [
  { id: "home", label: "Home", icon: "navigator" },
  { id: "chapters", label: "Chapters", icon: "chapters" },
  { id: "awardees", label: "Awardees", icon: "map" },
  { id: "donate", label: "Donate", icon: "donate" },
];

const SocialGlyph = ({ path }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d={path} />
  </svg>
);

const START_LINKS = [
  {
    label: "LinkedIn",
    href: FOOTER_LINKS.linkedin,
    icon: <SocialGlyph path="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />,
  },
  {
    label: "Instagram",
    href: FOOTER_LINKS.instagram,
    icon: <SocialGlyph path="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />,
  },
  {
    label: "Newsletter",
    href: FOOTER_LINKS.newsletter,
    icon: <SocialGlyph path="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />,
  },
];

export default function MobileLanding({ initialBootDone = false }) {
  // Boot sequence state
  const [bootSequence, setBootSequence] = useState(initialBootDone ? "complete" : "booting");
  const [desktopReady, setDesktopReady] = useState(initialBootDone);
  
  // State management
  const [currentTime, setCurrentTime] = useState("");
  const [activeApp, setActiveApp] = useState(null);
  const [lpApplicationChapter, setLpApplicationChapter] = useState("");
  const [activeTab, setActiveTab] = useState("home");
  const [history, setHistory] = useState(["home"]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showBuddyMessenger, setShowBuddyMessenger] = useState(false);
  const [showLPPortal, setShowLPPortal] = useState(false);
  const [awardees, setAwardees] = useState(loadCachedAwardees);
  const [filteredAwardees, setFilteredAwardees] = useState(loadCachedAwardees);
  const [awardeesLoading, setAwardeesLoading] = useState(false);
  const [awardeeChapterFilter, setAwardeeChapterFilter] = useState("");
  const [awardeeSort, setAwardeeSort] = useState("alpha");
  const [awardeeSearchTerm, setAwardeeSearchTerm] = useState("");
  const [marqueeRef, setMarqueeRef] = useState(null);
  const [awardeesMarqueeRef, setAwardeesMarqueeRef] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const startRef = useRef(null);

  // Live all-chapter totals from publicWinners; hardcoded copy until they load.
  const impactStats = useLandingImpactStats();

  // Close the Start menu on outside tap or Escape (mirrors DesktopTaskbar).
  useEffect(() => {
    if (!startOpen) return;
    const onPointerDown = (e) => {
      if (startRef.current && !startRef.current.contains(e.target)) {
        setStartOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setStartOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [startOpen]);

  // Chapter list — loaded from /chapters Firestore collection; falls back to the
  // hardcoded legacy cards (below) while loading or if the fetch fails. The four
  // legacy chapters have their original prose here so behavior is unchanged when
  // the collection is empty.
  const [mobileChapterCards, setMobileChapterCards] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'chapters'));
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(c => c.active !== false)
          .sort((a, b) => {
            const ao = typeof a.order === 'number' ? a.order : 999;
            const bo = typeof b.order === 'number' ? b.order : 999;
            if (ao !== bo) return ao - bo;
            return (a.name || '').localeCompare(b.name || '');
          })
          .map(c => ({
            name: c.name,
            foundedYear: c.foundedYear,
            tagline: c.tagline,
            pageSlug: c.pageSlug || c.id,
          }));
        if (!cancelled && list.length > 0) setMobileChapterCards(list);
      } catch (e) {
        console.error('MobileLanding: failed to load /chapters, using fallback', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  
  // Boot sequence handling
  const handleBootProgress = (isPreloading) => {
    if (isPreloading) {
      setBootSequence("preloaded");
    } else {
      setBootSequence("complete");
      setTimeout(() => {
        setDesktopReady(true);
      }, 300);
    }
  };

  // Clock functionality
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const yy = "03"; // hardcoded year — part of the Y2K-era aesthetic
      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
      setCurrentTime(`${mm}/${dd}/${yy} ${timeStr}`);
    };
    
    updateClock(); // Call once immediately
    const interval = setInterval(updateClock, 60000); // Then every minute
    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  // Navigation functions
  const setPage = (page) => {
    const newHistory = [...history.slice(0, historyIndex + 1), page];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setActiveTab(page); // Keep tab and page in sync
  };
  
  const goBack = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setActiveTab(history[historyIndex - 1]); // Update active tab
    }
  };
  
  const goForward = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setActiveTab(history[historyIndex + 1]); // Update active tab
    }
  };

  // Handle app opening (without sound)
  const handleOpenApp = (appName) => {
    setActiveApp(appName);
    // Removed sound effects for mobile
  };

  // Close active app and return to desktop
  const handleCloseApp = () => {
    setActiveApp(null);
  };

  // Load awardees from Firestore - memoized with useCallback
  const fetchAwardees = useCallback(async () => {
    // Only show the spinner if we have nothing cached to render
    if (awardees.length === 0) setAwardeesLoading(true);

    try {
      // Public projection of published winners only (see publicWinners rules /
      // syncPublicWinner). `pitches` is now reviewer-gated.
      const q = query(collection(db, "publicWinners"));
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setAwardees([]);
        setFilteredAwardees([]);
        setAwardeesLoading(false);
        return;
      }
      
      const awardeesData = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        // Hide winners staged in the admin Grant awards tab but not yet
        // published. Missing field reads as published (existing winners).
        if (data.winnerPublished === false) return;

        let quarter = "Unknown Quarter";
        let createdAtMs = 0;
        if (data.createdAt) {
          try {
            const date = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
            const q = Math.floor(date.getMonth() / 3) + 1;
            quarter = `Q${q} ${date.getFullYear()}`;
            createdAtMs = date.getTime();
          } catch (e) {
            console.error("Error parsing date for doc " + doc.id, e);
          }
        }

        awardeesData.push({
          id: doc.id,
          businessName: data.businessName || "Unnamed Business",
          founderName: data.founderName || "Unknown Founder",
          about: data.about || "",
          website: data.website || "",
          photoUrl: data["pitch-photo"] || data.founderPhotoUrl || "",
          chapter: data.chapter || "Unknown Chapter",
          quarter: quarter,
          createdAtMs: createdAtMs
        });
      });

      setAwardees(awardeesData);
      try {
        sessionStorage.setItem(AWARDEES_CACHE_KEY, JSON.stringify(awardeesData));
      } catch {
        // quota/serialization failure is non-fatal
      }
    } catch (error) {
      console.error("Error fetching awardees:", error);
    }

    setAwardeesLoading(false);
  }, []);

  // Prefetch awardees on mount (stale-while-revalidate using sessionStorage cache)
  useEffect(() => {
    fetchAwardees();
  }, [fetchAwardees]);

  // Filter + sort awardees
  useEffect(() => {
    if (awardees.length > 0) {
      const filtered = awardees.filter(awardee => {
        const matchesSearch = awardeeSearchTerm === "" ||
          awardee.businessName.toLowerCase().includes(awardeeSearchTerm.toLowerCase()) ||
          awardee.founderName.toLowerCase().includes(awardeeSearchTerm.toLowerCase());

        const matchesChapter = awardeeChapterFilter === "" ||
          awardee.chapter === awardeeChapterFilter;

        return matchesSearch && matchesChapter;
      });

      const sorted = [...filtered].sort((a, b) => {
        if (awardeeSort === "newest") return b.createdAtMs - a.createdAtMs;
        if (awardeeSort === "oldest") return a.createdAtMs - b.createdAtMs;
        return a.businessName.localeCompare(b.businessName);
      });

      setFilteredAwardees(sorted);
    }
  }, [awardeeSearchTerm, awardeeChapterFilter, awardeeSort, awardees]);

  // Marquee effects for press mentions and awardees
  useEffect(() => {
    if (activeTab === "home") {
      // Press mentions marquee animation
      const animatePressMarquee = () => {
        if (marqueeRef) {
          if (marqueeRef.scrollLeft >= marqueeRef.scrollWidth / 2) {
            marqueeRef.scrollLeft = 0;
          } else {
            marqueeRef.scrollLeft += 1;
          }
        }
      };
      
      // Awardees marquee animation - slightly slower speed
      const animateAwardeesMarquee = () => {
        if (awardeesMarqueeRef) {
          if (awardeesMarqueeRef.scrollLeft >= awardeesMarqueeRef.scrollWidth / 2) {
            awardeesMarqueeRef.scrollLeft = 0;
          } else {
            awardeesMarqueeRef.scrollLeft += 0.7; // Slightly slower speed
          }
        }
      };
      
      const pressIntervalId = marqueeRef ? setInterval(animatePressMarquee, 30) : null;
      const awardeesIntervalId = awardeesMarqueeRef ? setInterval(animateAwardeesMarquee, 30) : null;
      
      return () => {
        if (pressIntervalId) clearInterval(pressIntervalId);
        if (awardeesIntervalId) clearInterval(awardeesIntervalId);
      };
    }
  }, [marqueeRef, awardeesMarqueeRef, activeTab]);

  // Start menu handlers — a Navigator section closes any open program so the
  // section is actually visible; apps/overlays open on top.
  const handleStartSection = (id) => {
    setStartOpen(false);
    setActiveApp(null);
    setPage(id);
  };
  const handleStartApp = (open) => {
    setStartOpen(false);
    open();
  };

  // The single "window" button on the taskbar names what's on screen.
  const taskbarWindow =
    activeApp === "submit"
      ? { label: "Submit Pitch", icon: "pitch" }
      : activeApp === "lpApplication"
        ? { label: "LP Application", icon: "note" }
        : activeApp === "resources"
          ? { label: "Resources", icon: "resources" }
          : { label: "Neighborhood Navigator", icon: "navigator" };


  // Render appropriate content based on active tab
  // Common style for all tab content
  const commonFontStyle = {
    fontFamily: '"MS Sans Serif", "Segoe UI", Tahoma, Verdana, sans-serif'
  };

  const renderTabContent = () => {
    const currentPage = activeTab;

    switch (currentPage) {
      case "home":
        return (
          <main className="mb-content">
            <h1 style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>{SEO_H1}</h1>

            {/* ===== HERO — Ink ===== */}
            <section className="mb-block mb-block-magenta" style={{ padding: "36px 20px", background: "var(--mb-ink)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-aqua)" }}>
                  {HOME_HERO.eyebrow}<span className="mb-cursor" aria-hidden="true" />
                </span>
                <h2 className="mb-h1" style={{ color: "var(--mb-chalk)", fontSize: 32, lineHeight: 1.05 }}>
                  <span className="mb-figure">{HOME_HERO.headline.figure}</span>{HOME_HERO.headline.mid}<em className="mb-key">{HOME_HERO.headline.key}</em>{HOME_HERO.headline.tail}
                </h2>
                <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.95, fontSize: 15 }}>
                  {HOME_HERO.lede}
                </p>
                <button
                  type="button"
                  onClick={() => handleOpenApp("submit")}
                  className="mb-btn"
                  style={{ alignSelf: "flex-start", fontSize: 12, padding: "12px 18px", marginTop: 4, borderColor: "var(--mb-chalk)" }}
                >
                  {HOME_HERO.cta}
                  <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                </button>
              </div>
            </section>

            {/* ===== MISSION — Paper ===== */}
            <section className="mb-block mb-block-paper" style={{ padding: "36px 20px" }}>
              <div className="mb-section-head" style={{ margin: 0, marginBottom: 18 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{MISSION.eyebrow}</span>
                <h2 className="mb-h2" style={{ fontSize: 26, lineHeight: 1.1 }}>
                  {MISSION.headline}
                </h2>
              </div>
              <p className="mb-lede" style={{ fontSize: 15, marginBottom: 14 }}>
                {MISSION.lede.pre}<strong style={{ fontWeight: 600 }}>{MISSION.lede.strong}</strong>{MISSION.lede.post}
              </p>
              <p className="mb-body" style={{ fontSize: 13, marginBottom: 12 }}>
                {MISSION.body.pre}<em>{MISSION.body.em}</em>{MISSION.body.post}
              </p>
              <p className="mb-italic" style={{ fontSize: 15, color: "var(--mb-magenta)", margin: "8px 0" }}>
                {MISSION.tagline}
              </p>
            </section>

            {/* ===== IMPACT — Grape ===== */}
            <section className="mb-block mb-block-grape" style={{ padding: "36px 20px" }}>
              <div className="mb-section-head" style={{ textAlign: "center", alignItems: "center", marginBottom: 24 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-butter)" }}>{IMPACT.eyebrow}</span>
                <h2 className="mb-h2" style={{ color: "var(--mb-chalk)", fontSize: 26, lineHeight: 1.1 }}>
                  {IMPACT.headline.pre}<em className="mb-key" style={{ color: "var(--mb-aqua)" }}>{IMPACT.headline.key}</em>{IMPACT.headline.post}
                </h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {impactStats.map((s) => (
                  <div key={s.label} className="mb-stat">
                    <span className="mb-stat-num" style={{ color: "var(--mb-butter)", fontSize: 26 }}>{s.n}</span>
                    <span className="mb-stat-label" style={{ color: "var(--mb-chalk)", opacity: 0.85 }}>{MOBILE_STAT_LABELS[s.label] || s.label}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* ===== HOW IT WORKS — Aqua ===== */}
            <section className="mb-block mb-block-aqua" style={{ padding: "36px 20px" }}>
              <div className="mb-section-head" style={{ marginBottom: 20 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-ink)" }}>{HOW_IT_WORKS.eyebrow}</span>
                <h2 className="mb-h2" style={{ fontSize: 26, lineHeight: 1.1 }}>
                  {HOW_IT_WORKS.headline}
                </h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {MOBILE_HOW_IT_WORKS_STEPS.map((step) => (
                  <article key={step.n} className="mb-card mb-card-sm" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span className="mb-badge">Step / {step.n}</span>
                    <h3 className="mb-h3" style={{ marginTop: 2, fontSize: 22 }}>{step.title}</h3>
                    <p className="mb-body" style={{ margin: 0, fontSize: 13 }}>{step.copy}</p>
                  </article>
                ))}
              </div>
              <p className="mb-italic" style={{ marginTop: 24, fontSize: 15, textAlign: "center", color: "var(--mb-ink)" }}>
                This is not venture capital, and we expect no return.<br />
                This is <strong style={{ fontFamily: "var(--font-content)", fontStyle: "normal", fontWeight: 700 }}>belief capital</strong>.
              </p>
            </section>

            {/* ===== PRESS — Paper ===== */}
            <section className="mb-block mb-block-paper" style={{ padding: "28px 20px" }}>
              <div className="mb-section-head" style={{ marginBottom: 16, alignItems: "center", textAlign: "center" }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{PRESS.eyebrow}</span>
                <h2 className="mb-h3" style={{ fontSize: 20 }}>
                  {PRESS.headline}
                </h2>
              </div>
              <div
                ref={ref => setMarqueeRef(ref)}
                style={{ display: "flex", alignItems: "center", gap: 20, overflow: "hidden", whiteSpace: "nowrap" }}
              >
                <div style={{ display: "flex", gap: 20 }}>
                  {PRESS_ITEMS.map((press, index) => (
                    <a
                      key={`press-${press.title}-${index}`}
                      href={press.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ opacity: 0.8, height: "40px", display: "flex", alignItems: "center", flex: "0 0 auto", filter: "grayscale(100%)" }}
                    >
                      {press.logo ? (
                        <img src={press.logo} alt={`${press.title} logo - Good Neighbor Fund press coverage`} style={{ height: "100%", maxWidth: "120px", objectFit: "contain" }} />
                      ) : (
                        <span className="mb-numeral" style={{ fontSize: 12, color: "var(--mb-ink)" }}>{press.title}</span>
                      )}
                    </a>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 20 }}>
                  {PRESS_ITEMS.map((press, index) => (
                    <a
                      key={`dup-press-${press.title}-${index}`}
                      href={press.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ opacity: 0.8, height: "40px", display: "flex", alignItems: "center", flex: "0 0 auto", filter: "grayscale(100%)" }}
                    >
                      {press.logo ? (
                        <img src={press.logo} alt={`${press.title} logo - Good Neighbor Fund press coverage`} style={{ height: "100%", maxWidth: "120px", objectFit: "contain" }} />
                      ) : (
                        <span className="mb-numeral" style={{ fontSize: 12, color: "var(--mb-ink)" }}>{press.title}</span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            </section>

            {/* ===== AWARDEES MARQUEE — Butter ===== */}
            <section className="mb-block mb-block-butter" style={{ padding: "32px 0 32px 20px" }}>
              <div className="mb-section-head" style={{ marginBottom: 20, paddingRight: 20 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{AWARDEES_MARQUEE_HEAD.eyebrow} · {impactStats[0].n} strong</span>
                <h2 className="mb-h2" style={{ fontSize: 26, lineHeight: 1.1, maxWidth: "18ch" }}>
                  {AWARDEES_MARQUEE_HEAD.headline}
                </h2>
              </div>
              <div
                ref={ref => setAwardeesMarqueeRef(ref)}
                style={{ overflow: "hidden", whiteSpace: "nowrap" }}
              >
                <div style={{ display: "inline-flex", gap: 14 }}>
                  {MOBILE_AWARDEE_MARQUEE.map((img, index) => (
                    <img
                      key={`awardee-img-${index}`}
                      src={img.src}
                      alt={img.alt}
                      style={{
                        height: 130,
                        width: 130,
                        border: "2px solid var(--mb-ink)",
                        boxShadow: "var(--shadow-hard-sm)",
                        objectFit: "cover",
                        flex: "0 0 auto",
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: "inline-flex", gap: 14, marginLeft: 14 }}>
                  {MOBILE_AWARDEE_MARQUEE.map((img, index) => (
                    <img
                      key={`dup-awardee-img-${index}`}
                      src={img.src}
                      alt={img.alt}
                      aria-hidden="true"
                      style={{
                        height: 130,
                        width: 130,
                        border: "2px solid var(--mb-ink)",
                        boxShadow: "var(--shadow-hard-sm)",
                        objectFit: "cover",
                        flex: "0 0 auto",
                      }}
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* ===== TESTIMONIAL — Paper ===== */}
            <section className="mb-block mb-block-paper" style={{ padding: "36px 20px" }}>
              <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)", display: "block", marginBottom: 20, textAlign: "center" }}>
                {TESTIMONIAL.eyebrow}
              </span>
              <blockquote style={{ fontFamily: "var(--font-serif)", fontWeight: 500, fontSize: 20, lineHeight: 1.35, letterSpacing: "-0.01em", margin: 0, textAlign: "center" }}>
                <span style={{ fontFamily: "var(--font-serif)", color: "var(--mb-magenta)", fontSize: "1.6em", lineHeight: 0, position: "relative", top: "0.4em", marginRight: "0.08em" }}>"</span>
                {TESTIMONIAL.quote}
                <span style={{ fontFamily: "var(--font-serif)", color: "var(--mb-magenta)", fontSize: "1.6em", lineHeight: 0, position: "relative", top: "0.4em", marginLeft: "0.05em" }}>"</span>
              </blockquote>
              <div style={{ marginTop: 22, display: "flex", justifyContent: "center", alignItems: "center", gap: 10 }}>
                <span style={{ width: 22, height: 2, background: "var(--mb-ink)", display: "inline-block" }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "var(--font-content)", fontWeight: 700, fontSize: 14, color: "var(--mb-ink)" }}>
                    {TESTIMONIAL.name}
                  </div>
                  <div className="mb-numeral" style={{ fontSize: 11, color: "var(--mb-ink-60)", marginTop: 2 }}>
                    {TESTIMONIAL.role}
                  </div>
                </div>
                <span style={{ width: 22, height: 2, background: "var(--mb-ink)", display: "inline-block" }} />
              </div>
            </section>

            {/* ===== FINAL CTA — Ink ===== */}
            <section className="mb-block mb-block-ink" style={{ padding: "40px 20px", textAlign: "center" }}>
              <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)", display: "block", marginBottom: 14 }}>
                {FINAL_CTA.eyebrow}<span className="mb-cursor" aria-hidden="true" />
              </span>
              <h2 className="mb-h1" style={{ color: "var(--mb-chalk)", fontSize: 28, lineHeight: 1.1, margin: "0 auto 16px" }}>
                {FINAL_CTA.headline.pre}<em className="mb-key" style={{ color: "var(--mb-butter)" }}>{FINAL_CTA.headline.key}</em>
              </h2>
              <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.85, margin: "0 auto 24px", fontSize: 14 }}>
                No business plan required, just a 60-second pitch video and your authentic vision.
              </p>
              <button type="button" onClick={() => handleOpenApp("submit")} className="mb-btn" style={{ fontSize: 12, padding: "12px 18px" }}>
                {FINAL_CTA.cta}
                <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
              </button>
            </section>

            {/* ===== FOOTER — Ink ===== */}
            <footer className="mb-block mb-block-ink" style={{ padding: "20px 20px 24px", textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.15)" }}>
              <HitCounter />
              <div className="mb-numeral" style={{ marginTop: 12, fontSize: 10, color: "rgba(255,255,255,0.7)", letterSpacing: "0.05em" }}>
                &copy; {new Date().getFullYear()} {COPYRIGHT_NOTICE}
              </div>
            </footer>
          </main>
        );

      case "chapters": {
        const chapterCards = mobileChapterCards || LEGACY_CHAPTER_CARDS;

        return (
          <main className="mb-content">
            {/* ===== HERO — Grape ===== */}
            <section className="mb-block mb-block-grape" style={{ padding: "36px 20px" }}>
              <div className="mb-section-head" style={{ margin: 0 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-butter)" }}>{CHAPTERS_HERO.eyebrow}</span>
                <h2 className="mb-h1" style={{ color: "var(--mb-chalk)", fontSize: 32, lineHeight: 1.05 }}>
                  {CHAPTERS_HERO.headline.pre}<em className="mb-key" style={{ color: "var(--mb-aqua)" }}>{CHAPTERS_HERO.headline.key}</em>
                </h2>
                <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.9, marginTop: 10, fontSize: 15 }}>
                  {CHAPTERS_HERO.lede}
                </p>
              </div>
            </section>

            {/* ===== CHAPTER CARDS — Paper ===== */}
            <section className="mb-block mb-block-paper" style={{ padding: "28px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {chapterCards.map((c) => (
                  <article key={c.name} className="mb-card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                      <h3 className="mb-h3" style={{ margin: 0, fontSize: 22 }}>{c.name}</h3>
                      {c.foundedYear && (
                        <span className="mb-numeral" style={{ fontSize: 11, color: "var(--mb-ink-60)", whiteSpace: "nowrap" }}>
                          Est. {c.foundedYear}
                        </span>
                      )}
                    </div>
                    {c.tagline && (
                      <p className="mb-body" style={{ margin: 0, fontSize: 13 }}>{c.tagline}</p>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                      <button
                        type="button"
                        onClick={() => window.open(`/${c.pageSlug}`, '_blank')}
                        className="mb-btn mb-btn-ink"
                        style={{ padding: "10px 14px", fontSize: 11 }}
                      >
                        {CHAPTER_CARD_CTAS.visit}
                        <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setLpApplicationChapter(c.name); setActiveApp("lpApplication"); }}
                        className="mb-btn mb-btn-chalk"
                        style={{ padding: "10px 14px", fontSize: 11 }}
                      >
                        {CHAPTER_CARD_CTAS.join}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* ===== HOW CHAPTERS WORK — Aqua ===== */}
            <section className="mb-block mb-block-aqua" style={{ padding: "36px 20px" }}>
              <div className="mb-section-head" style={{ marginBottom: 14 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-ink)" }}>{HOW_CHAPTERS_WORK.eyebrow}</span>
                <h2 className="mb-h2" style={{ fontSize: 26, lineHeight: 1.1 }}>
                  {HOW_CHAPTERS_WORK.headline}
                </h2>
              </div>
              <img
                src={HOW_CHAPTERS_WORK.image.src}
                alt={HOW_CHAPTERS_WORK.image.alt}
                width="800"
                height="600"
                loading="lazy"
                decoding="async"
                style={{ width: "100%", height: "auto", display: "block", border: "var(--border-ink-2)", marginBottom: 16 }}
              />
              <p className="mb-lede" style={{ fontSize: 14 }}>
                {HOW_CHAPTERS_WORK.lede}
              </p>
            </section>

            {/* ===== COLLECTIVE GIVING — Paper ===== */}
            <section className="mb-block mb-block-paper" style={{ padding: "32px 20px" }}>
              <div className="mb-section-head" style={{ margin: 0, marginBottom: 16 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{COLLECTIVE_GIVING.eyebrow}</span>
                <h2 className="mb-h2" style={{ fontSize: 26, lineHeight: 1.1 }}>{COLLECTIVE_GIVING.headline}</h2>
              </div>
              <img
                src={COLLECTIVE_GIVING.image.src}
                alt={COLLECTIVE_GIVING.image.alt}
                width="800"
                height="600"
                loading="lazy"
                decoding="async"
                style={{ width: "100%", height: "auto", display: "block", border: "var(--border-ink-2)", marginBottom: 16 }}
              />
              <p className="mb-lede" style={{ fontSize: 15, marginBottom: 12 }}>
                {COLLECTIVE_GIVING.lede}
              </p>
              <p className="mb-body" style={{ fontSize: 13, marginBottom: 10 }}>
                {COLLECTIVE_GIVING.body1}
              </p>
              <p className="mb-body" style={{ fontSize: 13, marginBottom: 10 }}>
                {COLLECTIVE_GIVING.body2.pre}<strong style={{ fontWeight: 600 }}>{COLLECTIVE_GIVING.body2.strong}</strong>{COLLECTIVE_GIVING.body2.post}
              </p>
              <p className="mb-body" style={{ fontSize: 13 }}>
                Each quarter, LPs come together to review applications, share dinner, and vote on who receives new micro-grants.
              </p>
            </section>

            {/* ===== START A CHAPTER — Magenta ===== */}
            <section className="mb-block mb-block-magenta" style={{ padding: "40px 20px", textAlign: "center" }}>
              <span className="mb-eyebrow" style={{ color: "var(--mb-butter)", display: "block", marginBottom: 14 }}>
                {START_A_CHAPTER.eyebrow}
              </span>
              <h2 className="mb-h1" style={{ color: "var(--mb-chalk)", fontSize: 28, lineHeight: 1.1, margin: "0 auto 16px" }}>
                {START_A_CHAPTER.headline.pre}<em className="mb-key" style={{ color: "var(--mb-butter)" }}>{START_A_CHAPTER.headline.key}</em>
              </h2>
              <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.9, margin: "0 auto 24px", fontSize: 14 }}>
                {START_A_CHAPTER.lede}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => window.location.href = START_A_CHAPTER.primaryHref}
                  className="mb-btn mb-btn-butter"
                  style={{ fontSize: 12, padding: "12px 18px", width: "100%", maxWidth: 280 }}
                >
                  {START_A_CHAPTER.primaryCta}
                  <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                </button>
                <button
                  type="button"
                  onClick={() => window.open(START_A_CHAPTER.secondaryHref, "_blank")}
                  className="mb-btn mb-btn-chalk"
                  style={{ fontSize: 12, padding: "12px 18px", width: "100%", maxWidth: 280 }}
                >
                  {START_A_CHAPTER.secondaryCta}
                </button>
              </div>
            </section>
          </main>
        );
      }
      
        
      case "awardees":
        return (
          <main className="mb-content">
            {/* ===== HERO — Butter ===== */}
            <section className="mb-block mb-block-butter" style={{ padding: "32px 20px" }}>
              <div className="mb-section-head" style={{ margin: 0 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{AWARDEES_PAGE.eyebrow}</span>
                <h2 className="mb-h1" style={{ fontSize: 32, lineHeight: 1.05 }}>{AWARDEES_PAGE.headline}</h2>
                <p className="mb-lede" style={{ marginTop: 10, fontSize: 15 }}>
                  Each of these entrepreneurs received <strong style={{ fontWeight: 600 }}>$1,000 in belief capital</strong> to bring their business idea to life, creating positive impact in their neighborhoods.
                </p>
              </div>
            </section>

            {/* ===== FILTER TOOLBAR — Paper ===== */}
            <section className="mb-block mb-block-paper" style={{ padding: "20px 20px 16px" }}>
              <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
                <input
                  type="search"
                  placeholder="Search by founder or business"
                  aria-label="Search awardees"
                  value={awardeeSearchTerm}
                  onChange={(e) => setAwardeeSearchTerm(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    fontFamily: "var(--font-content)",
                    fontSize: 16, /* 16px floor — smaller triggers iOS zoom-on-focus */
                    background: "var(--mb-chalk)",
                    color: "var(--mb-ink)",
                    border: "2px solid var(--mb-ink)",
                    boxShadow: "var(--shadow-hard-sm)",
                    boxSizing: "border-box",
                    width: "100%"
                  }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    value={awardeeChapterFilter}
                    onChange={(e) => setAwardeeChapterFilter(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      fontFamily: "var(--font-content)",
                      fontSize: 16,
                      background: "var(--mb-chalk)",
                      color: "var(--mb-ink)",
                      border: "2px solid var(--mb-ink)",
                      boxShadow: "var(--shadow-hard-sm)",
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    <option value="">All chapters</option>
                    {LEGACY_CHAPTER_CARDS.map(c => c.name).map(chapter => (
                      <option key={chapter} value={chapter}>{chapter}</option>
                    ))}
                  </select>
                  <select
                    value={awardeeSort}
                    onChange={(e) => setAwardeeSort(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      fontFamily: "var(--font-content)",
                      fontSize: 16,
                      background: "var(--mb-chalk)",
                      color: "var(--mb-ink)",
                      border: "2px solid var(--mb-ink)",
                      boxShadow: "var(--shadow-hard-sm)",
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    <option value="alpha">A–Z</option>
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                  </select>
                </div>
              </div>

              {awardeesLoading && (
                <div style={{ textAlign: "center", padding: "28px 0 4px" }}>
                  <span className="mb-numeral" style={{ fontSize: 12, color: "var(--mb-ink-60)", letterSpacing: "0.1em" }}>Loading awardees…</span>
                </div>
              )}

              {!awardeesLoading && filteredAwardees.length === 0 && (
                <div style={{ textAlign: "center", padding: "28px 16px 4px", marginTop: 14, border: "1px dashed var(--mb-ink)" }}>
                  <p className="mb-body" style={{ margin: 0, fontSize: 13 }}>No awardees match your search. Try adjusting your filters.</p>
                </div>
              )}
            </section>

            {/* ===== AWARDEE GRID — Paper ===== */}
            {!awardeesLoading && filteredAwardees.length > 0 && (
              <section className="mb-block mb-block-paper" style={{ padding: "4px 20px 28px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 168px), 1fr))", gap: 12 }}>
                  {filteredAwardees.map((awardee, i) => (
                    <article key={awardee.id} className="mb-card" style={{ padding: 0, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "var(--shadow-hard-sm)" }}>
                      {awardee.photoUrl && (
                        <div style={{ borderBottom: "2px solid var(--mb-ink)", background: "var(--mb-ink)" }}>
                          <img
                            src={awardee.photoUrl}
                            alt={`${awardee.businessName} - ${awardee.chapter} Good Neighbor Fund $1,000 grant recipient`}
                            width="400"
                            height="400"
                            loading={i < 4 ? "eager" : "lazy"}
                            fetchPriority={i < 4 ? "high" : "auto"}
                            decoding="async"
                            style={{ width: "100%", height: "auto", aspectRatio: "1 / 1", objectFit: "cover", display: "block" }}
                          />
                        </div>
                      )}
                      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                        <div className="mb-numeral" style={{ fontSize: 9, color: "var(--mb-ink-60)", display: "flex", justifyContent: "space-between", gap: 4, lineHeight: 1.2 }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{awardee.chapter}</span>
                          <span style={{ whiteSpace: "nowrap" }}>{awardee.quarter}</span>
                        </div>
                        <h3 style={{ margin: 0, fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 16, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
                          {awardee.businessName}
                        </h3>
                        <div className="mb-italic" style={{ fontSize: 11, color: "var(--mb-ink-60)" }}>
                          by {awardee.founderName}
                        </div>
                        {awardee.about && (
                          <p className="mb-body" style={{ fontSize: 11, lineHeight: 1.4, margin: "4px 0 0", flex: 1 }}>
                            {awardee.about}
                          </p>
                        )}
                        {awardee.website && (
                          <a
                            href={awardee.website.startsWith('http') ? awardee.website : `http://${awardee.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mb-btn mb-btn-chalk"
                            style={{ marginTop: 8, width: "100%", padding: "8px 10px", fontSize: 10 }}
                          >
                            Visit website
                            <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                          </a>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </main>
        );
        
      case "donate":
        return (
          <main className="mb-content">
            {/* ===== HERO — Magenta with grant stat ===== */}
            <section className="mb-block mb-block-magenta" style={{ padding: "36px 20px" }}>
              <div className="mb-section-head" style={{ margin: 0, marginBottom: 20 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-butter)" }}>{DONATE_HERO.eyebrow}</span>
                <h2 className="mb-h1" style={{ color: "var(--mb-chalk)", fontSize: 30, lineHeight: 1.05 }}>
                  {DONATE_HERO.headline.pre}<em className="mb-key" style={{ color: "var(--mb-butter)" }}>{DONATE_HERO.headline.key}</em>{DONATE_HERO.headline.post}
                </h2>
                <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.92, marginTop: 12, fontSize: 14 }}>
                  {DONATE_HERO.lede}
                </p>
              </div>
              <div style={{
                background: "var(--mb-ink)",
                border: "2px solid var(--mb-chalk)",
                padding: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-butter)" }}>{DONATE_HERO.stat.eyebrow}</span>
                <span className="mb-numeral" style={{ fontSize: 26, fontWeight: 700, color: "var(--mb-chalk)", letterSpacing: "-0.04em", lineHeight: 1 }}>
                  {DONATE_HERO.stat.figure}
                </span>
                <span className="mb-body" style={{ color: "var(--mb-chalk)", opacity: 0.85, marginTop: 6, fontSize: 13 }}>
                  {DONATE_HERO.stat.caption}
                </span>
              </div>
            </section>

            {/* ===== TWO PATHS — Paper ===== */}
            <section className="mb-block mb-block-paper" style={{ padding: "28px 20px" }}>
              <div className="mb-section-head" style={{ marginBottom: 18 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{GIVING.eyebrow}</span>
                <h2 className="mb-h2" style={{ fontSize: 26, lineHeight: 1.1 }}>{GIVING.headline}</h2>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* One-Time */}
                <article className="mb-card" style={{ padding: 0, display: "flex", flexDirection: "column" }}>
                  <div style={{ background: "var(--mb-aqua-soft)", borderBottom: "2px solid var(--mb-ink)", padding: "14px 18px" }}>
                    <span className="mb-eyebrow" style={{ color: "var(--mb-aqua-deep)" }}>{ONE_TIME_GIVING.eyebrow}</span>
                    <h3 className="mb-h3" style={{ margin: "6px 0 0", fontSize: 22 }}>{ONE_TIME_GIVING.headline}</h3>
                  </div>
                  <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
                    <p className="mb-body" style={{ margin: 0, fontSize: 13 }}>
                      {ONE_TIME_GIVING.body}
                    </p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "12px 14px", background: "var(--mb-paper-deep)", border: "1px solid var(--mb-ink)" }}>
                      <span className="mb-eyebrow" style={{ color: "var(--mb-ink)", width: "100%", marginBottom: 4 }}>
                        {ONE_TIME_GIVING.amountsLabel}
                      </span>
                      {ONE_TIME_GIVING.amounts.map((amt) => (
                        <span
                          key={amt}
                          className="mb-numeral"
                          style={{
                            padding: "3px 8px",
                            background: "var(--mb-chalk)",
                            border: "1px solid var(--mb-ink)",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "var(--mb-ink)",
                          }}
                        >
                          {amt}
                        </span>
                      ))}
                    </div>
                    <ChapterDonationChooser
                      accent="aqua"
                      renderTrigger={(onClick) => (
                        <button
                          type="button"
                          onClick={onClick}
                          className="mb-btn mb-btn-ink mb-btn-full"
                          style={{ fontSize: 12, padding: "12px 14px" }}
                        >
                          {ONE_TIME_GIVING.cta}
                          <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                        </button>
                      )}
                    />
                  </div>
                </article>

                {/* Club Membership */}
                <article className="mb-card" style={{ padding: 0, display: "flex", flexDirection: "column", position: "relative" }}>
                  <div style={{
                    position: "absolute",
                    top: -10,
                    right: 14,
                    background: "var(--mb-magenta)",
                    color: "var(--mb-chalk)",
                    padding: "3px 9px",
                    fontFamily: "var(--font-chrome)",
                    fontSize: 10,
                    letterSpacing: " 0.01em",
                    textTransform: "none",
                    border: "1px solid var(--mb-ink)",
                    zIndex: 1,
                  }}>
                    {CLUB_MEMBERSHIP.badge}
                  </div>
                  <div style={{ background: "var(--mb-grape-soft)", borderBottom: "2px solid var(--mb-ink)", padding: "14px 18px" }}>
                    <span className="mb-eyebrow" style={{ color: "var(--mb-grape-deep)" }}>{CLUB_MEMBERSHIP.eyebrow}</span>
                    <h3 className="mb-h3" style={{ margin: "6px 0 0", fontSize: 22 }}>{CLUB_MEMBERSHIP.headline}</h3>
                  </div>
                  <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span className="mb-numeral" style={{ fontSize: 34, fontWeight: 700, color: "var(--mb-grape-deep)", letterSpacing: "-0.04em", lineHeight: 1 }}>
                        {CLUB_MEMBERSHIP.price}
                      </span>
                      <span className="mb-numeral" style={{ fontSize: 12, color: "var(--mb-ink-60)" }}>{CLUB_MEMBERSHIP.priceUnit}</span>
                    </div>
                    <p className="mb-body" style={{ margin: 0, fontSize: 13 }}>
                      Join a community of neighbors who fund the next generation of founders. A steady $25/month keeps grants flowing all year.
                    </p>
                    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                      {CLUB_MEMBERSHIP.benefits.map(benefit => (
                        <li key={benefit} className="mb-body" style={{ position: "relative", paddingLeft: 18, fontSize: 12, lineHeight: 1.45 }}>
                          <span style={{ position: "absolute", left: 0, top: 5, width: 8, height: 8, background: "var(--mb-grape)", border: "1px solid var(--mb-ink)" }} />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={CLUB_MEMBERSHIP.stripeUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mb-btn mb-btn-ink mb-btn-full"
                      style={{ fontSize: 12, padding: "12px 14px" }}
                    >
                      {CLUB_MEMBERSHIP.cta}
                      <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                    </a>
                  </div>
                </article>
              </div>

              <div style={{
                marginTop: 22,
                padding: "12px 14px",
                border: "1px solid var(--mb-ink)",
                background: "var(--mb-paper-deep)",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}>
                <span className="mb-badge">Tax-deductible</span>
                <p className="mb-body" style={{ margin: 0, flex: 1, minWidth: 200, fontSize: 11, lineHeight: 1.5 }}>
                  Donations are tax-deductible. Processed through our fiscal sponsor, <strong style={{ fontWeight: 700 }}>BootSector</strong>, a 501(c)3
                  <span className="mb-numeral"> (EIN 85-4082950)</span>.
                </p>
              </div>
            </section>

            {/* ===== CORPORATE SPONSORSHIP — Aqua ===== */}
            <section className="mb-block mb-block-aqua" style={{ padding: "32px 20px" }}>
              <div className="mb-section-head" style={{ marginBottom: 18 }}>
                <span className="mb-eyebrow" style={{ color: "var(--mb-ink)" }}>{SPONSORSHIP.eyebrow}</span>
                <h2 className="mb-h2" style={{ fontSize: 26, lineHeight: 1.1 }}>
                  {SPONSORSHIP.headline.line1}<br/>{SPONSORSHIP.headline.line2}
                </h2>
                <p className="mb-body" style={{ marginTop: 10, fontSize: 13 }}>
                  Three tiers of support for businesses that want to plant flags alongside the founders in their community.
                </p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 22 }}>
                {SPONSORSHIP_TIERS.map((tier, i) => (
                  <article key={tier.tier} className="mb-card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6 }}>
                    <span className="mb-numeral" style={{ fontSize: 10, color: "var(--mb-magenta)", fontWeight: 700, letterSpacing: " 0.01em" }}>
                      Tier 0{i + 1}
                    </span>
                    <h3 style={{ margin: 0, fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 20, letterSpacing: "-0.01em" }}>
                      {tier.tier}
                    </h3>
                    <span className="mb-numeral" style={{ fontSize: 22, fontWeight: 700, color: "var(--mb-ink)", letterSpacing: "-0.03em" }}>
                      {tier.amount}
                    </span>
                    <p className="mb-body" style={{ margin: 0, fontSize: 12, lineHeight: 1.45 }}>
                      {tier.summary}
                    </p>
                  </article>
                ))}
              </div>

              <div style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={() => window.location.href = SPONSORSHIP.mailto}
                  className="mb-btn mb-btn-ink"
                  style={{ fontSize: 12, padding: "12px 18px" }}
                >
                  {SPONSORSHIP.cta}
                  <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                </button>
              </div>
            </section>

            {/* ===== CO-FOUNDER NOTE — Butter ===== */}
            <section className="mb-block mb-block-butter" style={{ padding: "32px 20px" }}>
              <img
                src={COFOUNDER_NOTE.image.src}
                alt={COFOUNDER_NOTE.image.alt}
                width="220"
                height="220"
                loading="lazy"
                decoding="async"
                style={{
                  width: 220,
                  maxWidth: "100%",
                  aspectRatio: "1 / 1",
                  objectFit: "cover",
                  display: "block",
                  border: "var(--border-ink-2)",
                  filter: "grayscale(100%)",
                  marginBottom: 20,
                }}
              />
              <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)", display: "block", marginBottom: 16 }}>
                {COFOUNDER_NOTE.eyebrow}
              </span>
              <blockquote
                className="mb-display"
                style={{ fontSize: 18, lineHeight: 1.35, margin: 0, borderLeft: "4px solid var(--mb-magenta)", paddingLeft: 16 }}
              >
                {COFOUNDER_NOTE.quote}
              </blockquote>
              <div style={{ marginTop: 18, paddingLeft: 20, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 18, height: 2, background: "var(--mb-ink)", display: "inline-block" }} />
                <div>
                  <div style={{ fontFamily: "var(--font-content)", fontWeight: 700, fontSize: 14, color: "var(--mb-ink)" }}>{COFOUNDER_NOTE.name}</div>
                  <div className="mb-numeral" style={{ fontSize: 10, color: "var(--mb-ink-60)" }}>
                    {COFOUNDER_NOTE.role}
                  </div>
                </div>
              </div>
            </section>
          </main>
        );

      default:
        return <div>Page not found</div>;
    }
  };

  // Boot screen
  if (bootSequence !== "complete" || !desktopReady) {
    return <BootScreen variant="mobile" onFinish={handleBootProgress} />;
  }

  return (
    <div className="mobile-desktop" style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxSizing: "border-box",
      paddingBottom: "var(--taskbar-h, 34px)"
    }}>
      {/* Removed sound effects for mobile */}

      {/* If app is active, render that instead of main content */}
      {activeApp === "submit" ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
          <PitchPage onClose={() => setActiveApp(null)} />
        </div>
      ) : activeApp === "lpApplication" ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
          <LPApplication
            onClose={() => setActiveApp(null)}
            initialChapter={lpApplicationChapter}
          />
        </div>
      ) : activeApp === "resources" ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
          <ResourceNavigator onClose={() => setActiveApp(null)} />
        </div>
      ) : (
        <>
          <div style={{
            flex: 1,
            minHeight: 0,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            padding: "10px 0",
          }}>
            {/* Main Content Window — Win98 chrome: raised bevel + gradient titlebar */}
            <div className="retro-window win95-window" style={{
              width: "94%",
              margin: "10px auto 10px auto",
              overflow: "hidden",
              flex: "1",
              minHeight: 0,
              display: "flex",
              flexDirection: "column"
            }}>
              {/* Window title bar — gradient chrome, matches desktop windows */}
              <div className="win95-titlebar" style={{ cursor: "default", flex: "none" }}>
                <OSIcon32 kind="navigator" size={15} className="win95-titlebar__icon" />
                <span className="win95-titlebar__label">Neighborhood Navigator</span>
              </div>

              {/* Browser Chrome — ink bar with nav buttons + URL */}
              <div className="mb-browser-bar">
                <div className="mb-browser-nav">
                  <button
                    className="mb-nav-btn"
                    onClick={goBack}
                    disabled={historyIndex === 0}
                    aria-label="Go back"
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <polygon points="10,2 10,14 3,8" fill="currentColor" />
                      <rect x="10" y="7" width="4" height="2" fill="currentColor" />
                    </svg>
                  </button>
                  <button
                    className="mb-nav-btn"
                    onClick={goForward}
                    disabled={historyIndex === history.length - 1}
                    aria-label="Go forward"
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <polygon points="6,2 6,14 13,8" fill="currentColor" />
                      <rect x="2" y="7" width="4" height="2" fill="currentColor" />
                    </svg>
                  </button>
                  <button
                    className="mb-nav-btn"
                    onClick={() => setPage("home")}
                    aria-label="Go to home page"
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <polygon points="8,1 15,7 13,7 13,14 9,14 9,10 7,10 7,14 3,14 3,7 1,7" fill="currentColor" />
                    </svg>
                  </button>
                </div>
                <div className="mb-browser-url">
                  <span className="mb-browser-url-tag">HTTPS</span>
                  <span className="mb-browser-url-path">
                    goodneighbor.fund{activeTab === "home" ? "/" : `/${activeTab}`}
                  </span>
                </div>
              </div>

              {/* Tab Navigation — pixel-font all-caps with magenta active */}
              <nav role="navigation" aria-label="Main navigation" className="mb-tabs">
                {[
                  { id: "home", label: "Home" },
                  { id: "chapters", label: "Chapters" },
                  { id: "awardees", label: "Awardees" },
                  { id: "donate", label: "Donate" },
                ].map(tab => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setPage(tab.id)}
                      className={`mb-tab ${isActive ? "is-active" : ""}`}
                      aria-current={isActive ? "page" : undefined}
                      style={{ padding: "10px 14px", fontSize: 10, flex: 1 }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </nav>

              {/* Window Content — edge-to-edge mb-block sections */}
              <div style={{ padding: 0, overflowY: "auto", overscrollBehavior: "contain", flex: 1, minHeight: 0, background: "var(--mb-paper)" }}>
                {renderTabContent()}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Win98 taskbar — fixed to the bottom, same classes as the desktop */}
      <div className="win95-taskbar-98" ref={startRef}>
        <button
          type="button"
          className="win95-taskbar-98__start"
          onClick={() => setStartOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={startOpen}
        >
          <img
            src="/assets/gnf-logo.png"
            alt=""
            width="16"
            height="16"
            style={{ display: "block" }}
          />
          Start
        </button>

        {startOpen && (
          <div className="win95-startmenu" role="menu" aria-label="Start menu">
            <div className="win95-startmenu__banner" aria-hidden="true">
              <span>NeighborhoodOS</span>
            </div>
            <div className="win95-startmenu__items">
              {START_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  role="menuitem"
                  className="win95-startmenu__item"
                  onClick={() => handleStartSection(section.id)}
                >
                  <OSIcon32 kind={section.icon} size={24} />
                  {section.label}
                </button>
              ))}
              <div className="win95-startmenu__divider" aria-hidden="true" />
              <button
                type="button"
                role="menuitem"
                className="win95-startmenu__item"
                onClick={() => handleStartApp(() => handleOpenApp("submit"))}
              >
                <OSIcon32 kind="pitch" size={24} />
                Submit Pitch
              </button>
              <button
                type="button"
                role="menuitem"
                className="win95-startmenu__item"
                onClick={() => handleStartApp(() => handleOpenApp("resources"))}
              >
                <OSIcon32 kind="resources" size={24} />
                Resources
              </button>
              <button
                type="button"
                role="menuitem"
                className="win95-startmenu__item"
                onClick={() => handleStartApp(() => setShowBuddyMessenger(true))}
              >
                <OSIcon32 kind="messenger" size={24} />
                Buddy Messenger
              </button>
              <button
                type="button"
                role="menuitem"
                className="win95-startmenu__item"
                onClick={() => handleStartApp(() => setShowLPPortal(true))}
              >
                <OSIcon32 kind="portal" size={24} />
                LP Portal
              </button>
              <div className="win95-startmenu__divider" aria-hidden="true" />
              {START_LINKS.map((link) => (
                <a
                  key={link.label}
                  role="menuitem"
                  className="win95-startmenu__item"
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setStartOpen(false)}
                >
                  {link.icon}
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="win95-toolbar__sep" aria-hidden="true" />

        <button
          type="button"
          className="win95-taskbar-btn is-active"
          aria-current="page"
          style={{ width: "auto", minWidth: 0, flex: "0 1 auto" }}
        >
          <OSIcon32 kind={taskbarWindow.icon} size={16} style={{ flex: "none" }} />
          <span className="win95-taskbar-btn__label">{taskbarWindow.label}</span>
        </button>

        <div style={{ flex: 1 }} />

        <div className="win95-taskbar-98__tray">
          <span className="retro-clock">{currentTime}</span>
        </div>
      </div>

      {/* Donate Modal */}
      {showDonateModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999
        }} onClick={() => setShowDonateModal(false)}>
          <div className="retro-window win95-window" style={{
            maxWidth: "85%",
            maxHeight: "80%",
            width: "300px",
            overflow: "auto",
            display: "flex",
            flexDirection: "column"
          }} onClick={(e) => e.stopPropagation()}>
            <div className="win95-titlebar" style={{ cursor: "default", flex: "none" }}>
              <OSIcon32 kind="donate" size={15} className="win95-titlebar__icon" />
              <span className="win95-titlebar__label">Donate to GNF</span>
              <div className="win95-titlebar__btns">
                <button
                  type="button"
                  className="win95-btn-sys win95-close-btn"
                  onClick={() => setShowDonateModal(false)}
                  aria-label="Close donate modal"
                >
                  ✕
                </button>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "15px" }}>
              <p>Support our micro-grant program:</p>
              <div style={{ width: "100%", maxWidth: "260px", marginTop: "10px" }}>
                <ChapterDonationChooser
                  renderTrigger={(onClick) => (
                    <button
                      type="button"
                      onClick={onClick}
                      className="mb-btn mb-btn-ink mb-btn-full"
                      style={{ fontSize: 12, padding: "12px 14px" }}
                    >
                      Donate
                      <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                    </button>
                  )}
                />
              </div>
              <p style={{ fontSize: "12px", color: "var(--mb-ink-60)", marginTop: "15px", textAlign: "center" }}>
                Tax deductible through our fiscal sponsor, BootSector (EIN: 85-4082950)
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Buddy Messenger Modal */}
      {showBuddyMessenger && (
        <MobileBuddyMessenger onClose={() => setShowBuddyMessenger(false)} />
      )}

      {/* LP Portal Modal */}
      {showLPPortal && (
        <MobileLPPortal onClose={() => setShowLPPortal(false)} />
      )}
    </div>
  );
}